import { DatePipe } from '@angular/common';
import { Component, ComponentFactoryResolver, EventEmitter, Input, OnDestroy, Output, Renderer2, ViewChild, ViewContainerRef } from '@angular/core';
import { Subscription } from 'rxjs';
import { ApiError, DataTablesService, MessageService } from '../../../../../core';
import { AvatarComponent } from '../../../../../domains/shared/components/avatar/avatar.component';
import { ArquivoExtensaoIconPipe } from '../../../extensoes/pipes';
import { ArquivoReferencia, isArquivoReferenciaPedidoExpedicao } from '../../../referencias';
import { ArquivoOptions } from '../../arquivo-options';
import { ArquivoSearch, ArquivoSearchTotal } from '../../arquivo-search';
import { ArquivoSearchOrderBy } from '../../arquivo-search-orderby';
import { ArquivoFilter } from '../../filter';
import { ArquivoAuthGuardService, ArquivoSearchHttpService, ArquivoService } from '../../services';

@Component({
    selector: 'sgt-arquivo-table',
    templateUrl: 'arquivo-table.component.html',
    styleUrls: ['./arquivo-table.component.less'],
    providers: [
        ArquivoExtensaoIconPipe
    ]
})
export class ArquivoTableComponent implements OnDestroy {
    // Filter
    @Input() referencia: ArquivoReferencia;
    @Input() idReferencia: number;
    @Input() filter: ArquivoFilter;
    // Table
    @Input() idTable = 'tableArquivos';
    dataTable: any;
    // Config
    /** Se pode excluir arquivos */
    @Input() canDelete = true;
    // Events
    @Output() delete = new EventEmitter<ArquivoSearch>();
    // Search
    isLoadingTotal: boolean;
    total: ArquivoSearchTotal;
    private searchSubs$: Subscription;
    isLoading: boolean;

    /** Eventos de abertura dos arquivos */
    private openEvents: JQuery<HTMLElement>[] = [];

    // Select
    /** Flag de todos os arquivos selecionados */
    selectedAll = false;
    /** Arquivos selecionados */
    selected: ArquivoSearch[] = [];
    /** Eventos de clicks dos checkbox */
    private checkboxEvents: JQuery<HTMLElement>[] = [];

    /** Container para criar dinâmicamente o avatar do usuário */
    @ViewChild('avatarContainer', {read: ViewContainerRef}) avatarContainer: ViewContainerRef;

    constructor(private componentFactoryResolver: ComponentFactoryResolver,
                private renderer: Renderer2,
                private datePipe: DatePipe,
                private dataTablesService: DataTablesService,
                private messageService: MessageService,
                private arquivoExtensaoIconPipe: ArquivoExtensaoIconPipe,
                private arquivoAuthGuardService: ArquivoAuthGuardService,
                private arquivoSearchHttpService: ArquivoSearchHttpService,
                private arquivoService: ArquivoService) { }

    /** Seta o filtro padrão para a pesquisa dos arquivos */
    private setDefaultFilter() {
        // Referência
        this.filter.referencia = this.referencia;
        this.filter.idReferencia = this.idReferencia;
    }

    search() {
        // Realizando unsubscribe dos subscriptions atuais e criando um novo para as próximas pesquisas
        this.searchSubs$?.unsubscribe();
        this.searchSubs$ = new Subscription();

        // Select
        this.selectedAll = false;
        this.selected = [];
        this.clearOpenEvents();
        this.clearCheckboxEvents();

        this.setDefaultFilter();
        // Obtendo os totais de arquivos
        this.getTotais()
            .then(() => {
                if (!this.dataTable) { // Table ainda não criada
                    this.createTable();
                } else { // Table já criada, somente damos um reload
                    this.dataTable.ajax.reload();
                }
            })
            .catch(() => {});
    }

    /** Obtem os totais dos arquivos */
    private getTotais(): Promise<ArquivoSearchTotal> {
        return new Promise<ArquivoSearchTotal>((resolve, reject) => {
            this.searchSubs$.add(this.arquivoSearchHttpService.getTotais(this.filter)
                .subscribe(
                    total => {
                        this.total = total;
                        this.isLoadingTotal = false;
                        resolve(total);
                    },
                    (error: ApiError) => {
                        this.isLoadingTotal = false;
                        this.messageService.error(error.message, 'Erro ao obter os totais dos arquivos', error);
                        reject();
                    }
                )
            );
        });
    }

    /** Cria a dataTable dos tipos de arquivos */
    createTable(): Promise<void> {
        return new Promise<void>((resolve) => {
            const columns: any[] = [
                {
                    data: null,
                    className: 'col-select text-center',
                    render: () => {
                        const template = `
                            <input type="checkbox" title="Selecionar" class="arquivoCheckboxSelect">
                        `
                        return template;
                    },
                    createdCell: (td: any, cellData: any, arquivo: ArquivoSearch) => {
                        // Obtendo valor do checkbox para saber se está selecionado ou não
                        const checkbox = jQuery(td).find('input[type="checkbox"]');
                        // Criando evento de click no checkbox
                        this.checkboxEvents.push(checkbox.on('click', () => {
                            // Setando o arquivo selecionado ou não
                            this.toggleSelected(arquivo, checkbox.is(':checked'));
                        }));
                    }
                },
                {
                    data: null,
                    className: 'col-options',
                    orderable: false,
                    render: (data, type, row, meta) => {
                        const arquivo = data as ArquivoSearch;

                        // Opções
                        const options = new ArquivoOptions();
                        // Abrir
                        options.canOpen = this.arquivoAuthGuardService.canOpen(arquivo.id);
                        // Download
                        // options.canDownload = this.arquivoAuthGuardService.canDownload(arquivo.id);
                        // Excluir
                        options.canDelete = this.canDelete && this.arquivoAuthGuardService.canDelete(arquivo.id, arquivo.idUsuarioCreated);

                        let template = '';
                        if (options.hasAnyOption()) {
                            template += `
                                <span class="btn-group dropup table-options">
                                    <button type="button" title="Opções" class="btn btn-link btn-options dropdown-toggle" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false">
                                        <i class="fas fa-ellipsis-h" aria-hidden="true"></i>
                                    </button>
                                    <div class="dropdown-menu">
                            `;
                            // Abrir
                            if (options.canOpen) {
                                template +=  `
                                    <a class="dropdown-item" id="open">
                                        <i class="fas fa-external-link-alt"></i> Abrir
                                    </a>
                                `;
                            }

                            // Download
                            // if (options.canOpen) {
                            //     template +=  `
                            //         <a class="dropdown-item" href="${arquivo.url}" target="_blank" download="${arquivo.nome}">
                            //             <i class="fas fa-download"></i> Download
                            //         </a>
                            //     `;
                            // }

                            // Excluir
                            if (options.canDelete) {
                                template +=  `
                                    <div class="dropdown-divider"></div>
                                    <a class="dropdown-item dropdown-item-delete" id="delete">
                                        <i class="far fa-trash-alt" aria-hidden="true"></i> Excluir
                                    </a>
                                `;
                            }

                            template += `
                                    </div>
                                </span>
                            `;
                        }

                        return template;
                    },
                    createdCell: (td, arquivo: ArquivoSearch, rowData, row, col) => {
                        const dropdown = jQuery(td).find('.btn-group');
                        // Ao abrir o dropdown, adicionamos ele no body para exibi-lo corretamente na table
                        dropdown.on('show.bs.dropdown', function() {
                            jQuery('body').append(dropdown.css({
                                    position: 'absolute',
                                    left: dropdown.offset().left,
                                    top: dropdown.offset().top
                                }).detach());
                        });
                        // Ai fechar o dropdown, adicionamos ele novamente a coluna das opções na table
                        dropdown.on('hidden.bs.dropdown', function() {
                            jQuery(td).append(dropdown.css({
                                position: 'relative',
                                left: 'auto',
                                top: 'auto'
                            }).detach());
                        });

                        // Vinculando os eventos de click das opções da tabela
                        const myself = this;
                        // Opções do dropdown
                        dropdown.on('click', 'a', function() {
                            if (this.id === 'open') {
                                myself.open(arquivo, true);
                            } else if (this.id === 'delete') {
                                myself.delete.emit(arquivo);
                            }
                        });
                    }
                },
                {
                    data: null,
                    className: 'col-nome',
                    serverOrderByColumn: ArquivoSearchOrderBy.Nome,
                    render: (arquivo: ArquivoSearch) => {
                        if (arquivo) {
                            return `
                                <span class="col-nome-container">
                                    <span class="col-nome-extensao">
                                        <i class="${this.arquivoExtensaoIconPipe.transform(arquivo.extensao)}" aria-hidden="true"></i>
                                    </span>
                                    <a class="col-nome-content">
                                        <span>
                                            ${arquivo.nome}

                                            ${isArquivoReferenciaPedidoExpedicao(arquivo.referencia) && arquivo.pedidoPendenteExpedicao ? `<span class="badge badge-danger">Pendente</span>` : ''}
                                        </span>
                                        <small>${arquivo.descricao ?? ''}</small>
                                    </a>
                                </span>
                            `;
                        }
                        return '';
                    },
                    createdCell: (td: any, cellData: any, arquivo: ArquivoSearch) => {
                        // Obtendo o link de abertura do arquivo
                        const link = jQuery(td).find('.col-nome-content');
                        // Criando evento de click no link
                        this.openEvents.push(link.on('click', async () => this.open(arquivo, true)));

                        if (arquivo) {
                            td.title = arquivo.nome;
                        }
                    }
                },
                {
                    data: 'nomeTipoArquivo',
                    className: 'col-tipo',
                    serverOrderByColumn: ArquivoSearchOrderBy.NomeTipoArquivo,
                    createdCell: (td, cellData, rowData, row, col) => {
                        if (cellData) {
                            td.title = cellData;
                        }
                    }
                },
                {
                    data: null,
                    className: 'col-usuario-created',
                    serverOrderByColumn: ArquivoSearchOrderBy.NomeUsuarioCreated,
                    render: () => '',
                    createdCell: (td: any, cellData: any, arquivo: ArquivoSearch) => {
                        if (arquivo) {
                            const factory = this.componentFactoryResolver.resolveComponentFactory(AvatarComponent);
                            const ref = this.avatarContainer.createComponent(factory);
                            ref.instance.settings.imageUrl = arquivo.avatarURLUsuarioCreated;
                            ref.instance.settings.fullName = arquivo.nomeUsuarioCreated;
                            ref.instance.settings.isShowLabelNameRight = true;
                            this.renderer.appendChild(td, ref.location.nativeElement);
                        }
                    }
                },
                {
                    data: 'createdAt',
                    className: 'col-created-at text-center',
                    serverOrderByColumn: ArquivoSearchOrderBy.NomeUsuarioCreated,
                    render: (data) => this.datePipe.transform(data, 'dd/MM/yyyy - HH:mm'),
                    createdCell: (td, cellData, rowData, row, col) => {
                        if (cellData) {
                            td.title = this.datePipe.transform(cellData, 'dd/MM/yyyy - HH:mm');
                        }
                    }
                },
            ];

            const columnDefs: any[] = [
                {orderable: false, targets: [0]}
            ];

            this.dataTablesService.showTable(`#${this.idTable}`, this.dataTable, {
                columnDefs,
                columns,
                order: [[5, 'desc']], // CreatedAt
                searching: false,
                info: false,
                serverSide: true,
                processing: true,
                deferRender: true,
                scroller: true,
                scrollX: true,
                // stateSave: true,
                ajax: (data, callback, settings) => {
                    // Filter
                    this.setDefaultFilter();
                    // Order By
                    const order = data?.order[0];
                    const orderColumn = columns[order?.column];
                    const orderBy = orderColumn?.serverOrderByColumn;
                    const orderByDir = order?.dir;
                    // Limit
                    const limit = data.length;
                    // Offset
                    const offset = data.start;

                    this.searchSubs$.add(this.arquivoSearchHttpService.search(this.filter, orderBy, orderByDir, limit, offset)
                        .subscribe(
                            entities => {
                                callback({recordsTotal: this.total?.quantidade, recordsFiltered: this.total?.quantidade, data: entities});
                            },
                            (error: ApiError) => {
                                this.messageService.error(error.message, 'Erro ao pesquisar os arquivos', error);
                                callback({recordsTotal: 0, recordsFiltered: 0, data: []});
                            }
                        )
                    );
                }
            }).then(dataTable => {
                this.dataTable = dataTable;
                this.dataTable
                    .on('preDraw', () => { // Antes de redesenhar a table
                        this.clearDynamicsComponents();
                    });

                    resolve();
            })
            .catch(() => {});
        });
    }

    /** Atualizando a pesquisa */
    refresh() {
        // Atualizando a pesquisa através da datatable
        this.dataTable?.draw(false);
        // Atualizando os totais
        this.getTotais()
            .then(() => {})
            .catch(() => {});
    }

    /** Altera a seleção do arquivo */
    toggleSelected(arquivo: ArquivoSearch, selected: boolean) {
        if (!arquivo) {
            return;
        }
        arquivo.selected = selected;
        if (arquivo.selected) {
            this.addSelected(arquivo);
        } else {
            this.removeSelected(arquivo);
        }
    }

    /** Adiciona o arquivo selecionado */
    private addSelected(arquivo: ArquivoSearch) {
        if (!arquivo || !arquivo.selected) {
            return;
        }
        this.selected.push(arquivo);
    }

    /** Remove o arquivo selecionado */
    private removeSelected(arquivo: ArquivoSearch) {
        if (!arquivo || arquivo.selected) {
            return;
        }
        const index = this.selected.findIndex(arquivoFilter => arquivoFilter.id === arquivo.id);
        if (index >= 0) {
            this.selected.splice(index, 1);
        }
    }

    /** Altera a seleção de todos os arquivos */
    toggleSelectedAll(selected: boolean) {
        this.selectedAll = selected;
        this.selected = [];

        const arquivos = this.dataTable.rows().data().toArray() as ArquivoSearch[];
        arquivos?.forEach(arquivo => {
            arquivo.selected = this.selectedAll;
            if (arquivo.selected) {
                this.addSelected(arquivo);
            }
        });

        jQuery('.arquivoCheckboxSelect').prop('checked', this.selectedAll);
    }

    /** Abre o arquivo */
    private async open(arquivo: ArquivoSearch, redrawTable: boolean) {
        arquivo = await this.arquivoService.open(arquivo);

        if (redrawTable) {
            this.dataTable.draw(false);
        }
    }

    /** Abre os arquivos selecionados */
    openSelected() {
        this.selected?.forEach(arquivo => this.open(arquivo, false));
        setTimeout(() => this.dataTable.draw(false), 100);
    }

    /** Imprime o arquivo */
    private async print(arquivo: ArquivoSearch, redrawTable: boolean) {
        arquivo = await this.arquivoService.print(arquivo);

        if (redrawTable) {
            this.dataTable.draw(false);
        }
    }

    /** Imprime os arquivos selecionados */
    printSelected() {
        this.selected?.forEach(arquivo => this.print(arquivo, false));
        setTimeout(() => this.dataTable.draw(false), 100);
    }

    /** Remove os eventos vinculados as aberturas do arquivos */
    private clearOpenEvents() {
        this.openEvents?.forEach(event => event.off('click'));
        this.openEvents = [];
    }

    /** Remove os eventos vinculados aos clicks dos checkbox criados */
    private clearCheckboxEvents() {
        this.checkboxEvents?.forEach(event => event.off('click'));
        this.checkboxEvents = [];
    }

    /** Destroi todos components criados dinâmicamente  */
    private clearDynamicsComponents() {
        this.avatarContainer?.clear();
    }

    ngOnDestroy() {
        this.searchSubs$?.unsubscribe();
        this.clearOpenEvents();
        this.clearCheckboxEvents();
        this.clearDynamicsComponents();
    }
}
